import React, { useState, useEffect } from 'react';
import { Copy, Check, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';

const generateReferralCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const ReferralLink: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
        if (session) {
          fetchReferralCode();
        }
      } catch (err) {
        console.error('Error checking auth:', err);
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const fetchReferralCode = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) return;

      const { data: codes, error: codesError } = await supabase
        .from('referral_codes')
        .select('code')
        .eq('user_id', user.id)
        .maybeSingle();

      if (codesError && codesError.code !== 'PGRST116') throw codesError;
      if (codes?.code) {
        setReferralCode(codes.code);
      }
    } catch (err) {
      console.error('Error fetching referral code:', err);
      setError('Failed to fetch referral code');
    }
  };

  const createReferralCode = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const code = generateReferralCode();

      const { error: insertError } = await supabase
        .from('referral_codes')
        .insert({
          user_id: user.id,
          code
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setReferralCode(code);
    } catch (err) {
      console.error('Error creating referral code:', err);
      setError('Failed to create referral code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralCode) return;

    try {
      const referralLink = `${window.location.origin}/restaurant-signup?ref=${referralCode}`;
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center p-4">
        <span>Bitte melden Sie sich an um einen Ref-Link zu erstellen</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">{error}</div>
    );
  }

  if (!referralCode) {
    return (
      <button
        onClick={createReferralCode}
        className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition"
      >
        <span>Erstelle Ref-Link</span>
      </button>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <div className="flex-1 bg-gray-100 px-4 py-2 rounded-md font-mono text-sm">
        {referralCode}
      </div>
      <button
        onClick={copyToClipboard}
        className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition"
        title="Copy referral link"
      >
        {copied ? (
          <Check className="h-5 w-5" />
        ) : (
          <Copy className="h-5 w-5" />
        )}
      </button>
    </div>
  );
};

export default ReferralLink;